import type { RoomPreview } from '@rummy/protocol';
import { useI18n, type MessageKey } from '../i18n/index.tsx';

const CRESTS: Record<RoomPreview['crest'], string> = {
  spade: '♠',
  lotus: '✦',
  sun: '☀',
  moon: '☾',
};

/** A room invitation should explain the place before it claims a seat. */
export function InviteLanding({
  preview,
  onJoin,
  onWatch,
  onCancel,
  onRules,
}: {
  preview: RoomPreview;
  onJoin: () => void;
  onWatch: () => void;
  onCancel: () => void;
  onRules: () => void;
}) {
  const { t } = useI18n();
  const live = preview.state === 'in_hand';
  /*
   * A table with no chair left still accepted the click and then silently
   * queued the arrival for the next session. Say so before they join, not
   * after: "Join table" that does not seat you is the button lying.
   */
  const full = !live && preview.seated >= preview.seats;
  return (
    <main className="invite-landing">
      <section className="invite-landing__card">
        <span className={`invite-landing__crest invite-landing__crest--${preview.crest}`} aria-hidden="true">
          {CRESTS[preview.crest]}
        </span>
        <p className="invite-landing__eyebrow">{t('invite.preview.eyebrow')}</p>
        <h1>{preview.label}</h1>
        <p className="invite-landing__host">{t('invite.preview.host', { name: preview.host })}</p>
        <div className="invite-landing__facts">
          <span>{t(`format.${preview.format}` as MessageKey)}</span>
          <span>{t('lobby.seatCount', { seated: preview.seated, total: preview.seats })}</span>
          <span className={live ? 'is-live' : ''}>{live ? t('invite.preview.live') : t('invite.preview.waiting')}</span>
        </div>
        <p className="invite-landing__copy">
          {live
            ? t('invite.preview.live.copy')
            : full
              ? t('invite.preview.full.copy')
              : t('invite.preview.waiting.copy')}
        </p>
        <div className="invite-landing__actions">
          {live && <button type="button" className="btn btn--primary btn--large" onClick={onWatch}>{t('invite.preview.watch')}</button>}
          <button
            type="button"
            className={live || full ? 'btn btn--large' : 'btn btn--primary btn--large'}
            onClick={onJoin}
          >
            {live || full ? t('invite.preview.joinNext') : t('invite.preview.join')}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onCancel}>{t('invite.preview.cancel')}</button>
        </div>
        {/* The one screen where somebody who has never played Rummy reliably
            turns up: they were sent a link by a friend. */}
        <button type="button" className="btn btn--link invite-landing__rules" onClick={onRules}>
          {t('rules.new')}
        </button>
      </section>
    </main>
  );
}
